import React from 'react';
import { Dialog, DialogContent, DialogActions, Box, Typography, Button, IconButton } from '@material-ui/core';
import { Close, FileCopy, Share, PersonAdd, SupervisorAccount, OpenInNew } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { useSnackbar } from '../config/useSnackbar';
import { copyTextToClipboard } from '../../common/CopyToClipboard';
import { useSetupDialogStyles } from './setupDialogStyles';

interface InviteVolunteerDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const useLocalStyles = makeStyles((theme: Theme) =>
  createStyles({
    linkBox: {
      display: 'flex',
      alignItems: 'center',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.grey[50],
      marginTop: theme.spacing(1.5),
    },
    linkText: {
      flex: 1,
      fontFamily: 'monospace',
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      wordBreak: 'break-all',
    },
    copyButton: {
      marginLeft: theme.spacing(1),
      textTransform: 'none',
      flexShrink: 0,
    },
    section: {
      marginBottom: theme.spacing(3),
    },
    sectionTitle: {
      fontWeight: 600,
      fontSize: 14,
      marginBottom: theme.spacing(0.5),
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.text.primary,
    },
    sectionIcon: {
      fontSize: 20,
      marginRight: theme.spacing(1),
      color: '#796fff',
    },
    sectionBody: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    screenLink: {
      display: 'inline-flex',
      alignItems: 'center',
      color: '#796fff',
      fontSize: '0.875rem',
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    linkIcon: {
      fontSize: 14,
      marginLeft: 3,
    },
    roleList: {
      margin: 0,
      paddingLeft: 20,
      marginTop: theme.spacing(1),
    },
    roleItem: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      marginBottom: theme.spacing(0.75),
      lineHeight: 1.5,
    },
    roleName: {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
  })
);

export function InviteVolunteerDialog({ open, onClose, onComplete }: InviteVolunteerDialogProps) {
  const classes = useSetupDialogStyles();
  const localClasses = useLocalStyles();
  const { setSnackbar } = useSnackbar();

  const applyUrl = `${window.location.origin}/apply`;

  const handleCopy = () => {
    copyTextToClipboard(applyUrl)
      .then(() => {
        setSnackbar({
          open: true,
          message: 'Copied to clipboard',
          variant: 'success',
        });
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'Failed to copy',
          variant: 'error',
        });
      });
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.header}>
        <IconButton className={classes.closeButton} onClick={onClose} size='small'>
          <Close fontSize='small' />
        </IconButton>
        <Typography className={classes.title}>Invite Volunteers & Team</Typography>
        <Typography className={classes.subtitle}>Get your volunteers and management team on board</Typography>
      </Box>

      <DialogContent className={classes.content}>
        {/* Section 1: Apply Link */}
        <Box className={localClasses.section}>
          <Typography className={localClasses.sectionTitle}>
            <Share className={localClasses.sectionIcon} />
            Share Your Apply Link
          </Typography>
          <Typography className={localClasses.sectionBody}>
            Volunteers use this link to apply to your organisation. They will create an account, complete any onboarding
            tasks you have configured, and then be available for you to roster onto activities.
          </Typography>
          <Box className={localClasses.linkBox}>
            <Typography className={localClasses.linkText}>{applyUrl}</Typography>
            <Button
              variant='outlined'
              size='small'
              startIcon={<FileCopy style={{ fontSize: 16 }} />}
              onClick={handleCopy}
              className={localClasses.copyButton}
            >
              Copy
            </Button>
          </Box>
        </Box>

        {/* Section 2: Manually Add Volunteers */}
        <Box className={localClasses.section}>
          <Typography className={localClasses.sectionTitle}>
            <PersonAdd className={localClasses.sectionIcon} />
            Manually Add Volunteers
          </Typography>
          <Typography className={localClasses.sectionBody}>
            If you have existing volunteers, you can add them directly. Head to the{' '}
            <a href='/management/volunteer-management' className={localClasses.screenLink}>
              Volunteer Database
              <OpenInNew className={localClasses.linkIcon} />
            </a>{' '}
            and click the <strong>Add</strong> button in the top right corner. Follow the prompts to enter their details
            and assign them to a program. They will receive an email invitation to complete their sign up.
          </Typography>
        </Box>

        {/* Section 3: Add Management / Team */}
        <Box className={localClasses.section}>
          <Typography className={localClasses.sectionTitle}>
            <SupervisorAccount className={localClasses.sectionIcon} />
            Add Your Management Team
          </Typography>
          <Typography className={localClasses.sectionBody}>
            To give team members management access, promote them from the relevant screens:
          </Typography>
          <Box component='ul' className={localClasses.roleList}>
            <li>
              <Typography className={localClasses.roleItem}>
                <span className={localClasses.roleName}>Admins</span> &mdash; Go to{' '}
                <a href='/admin-console/admin-management' className={localClasses.screenLink}>
                  Admin Console &gt; Admins
                  <OpenInNew className={localClasses.linkIcon} />
                </a>{' '}
                to give full management access
              </Typography>
            </li>
            <li>
              <Typography className={localClasses.roleItem}>
                <span className={localClasses.roleName}>Program Managers</span> &mdash; Go to{' '}
                <a href='/management/program-management' className={localClasses.screenLink}>
                  Program Management
                  <OpenInNew className={localClasses.linkIcon} />
                </a>{' '}
                to assign managers to specific programs
              </Typography>
            </li>
            <li>
              <Typography className={localClasses.roleItem}>
                <span className={localClasses.roleName}>Activity Team</span> &mdash; Go to{' '}
                <a href='/management/activity-management' className={localClasses.screenLink}>
                  Activity Management
                  <OpenInNew className={localClasses.linkIcon} />
                </a>{' '}
                to promote volunteers to activity leaders
              </Typography>
            </li>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Box />
        <Button className={classes.primaryButton} onClick={handleComplete}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
